/**
 * TEMPORARY utilities to extract structured sections from finePrint markdown text.
 *
 * These are temporary client-side workarounds. The extracted sections will
 * eventually be provided as structured fields from svc-offer -> svc-public-offer,
 * input via Admin Portal.
 */

const getBoldHeading = (paragraph: string): string | undefined => {
  return paragraph.match(/^\*\*([^*]+)\*\*/)?.[1]
}

/**
 * Extract cancellation policy sections from finePrint markdown text.
 * Matches paragraphs with bold headers containing "Cancellation" or "Refund"
 * keywords, plus any continuation paragraphs (e.g. the flights disclaimer).
 */
export function extractCancellationFromFinePrint(finePrint: string | undefined): {
  cancellation: string | undefined
  remaining: string
} {
  if (!finePrint) return { cancellation: undefined, remaining: '' }

  const paragraphs = finePrint.split('\n\n')
  const cancellation: Array<string> = []
  const remaining: Array<string> = []
  let prevWasCancellation = false

  for (const p of paragraphs) {
    const trimmed = p.trim()
    if (!trimmed) continue

    const heading = getBoldHeading(trimmed)
    const isCancellationHeader = heading ? /(?:cancellation|refund)/i.test(heading) : false
    // Continuation paragraph (no bold header) following a cancellation paragraph.
    // Includes bullet lists which are often part of the cancellation terms.
    // Known limitation: may capture unrelated paragraphs that happen to follow
    // a cancellation section. Acceptable until structured fields replace this extraction.
    const isContinuation = prevWasCancellation && !trimmed.startsWith('**')

    if (isCancellationHeader || isContinuation) {
      cancellation.push(trimmed)
      prevWasCancellation = true
    } else {
      remaining.push(trimmed)
      prevWasCancellation = false
    }
  }

  return {
    cancellation: cancellation.length ? cancellation.join('\n\n') : undefined,
    remaining: remaining.join('\n\n'),
  }
}

/**
 * Extract inclusion notes from finePrint markdown text.
 * Matches the "**Please Note:**" heading and its following bullet list,
 * which typically contains details about how to use the included benefits.
 */
export function extractInclusionNotesFromFinePrint(finePrint: string | undefined): {
  inclusionNotes: string | undefined
  remaining: string
} {
  if (!finePrint) return { inclusionNotes: undefined, remaining: '' }

  const paragraphs = finePrint.split('\n\n')
  const inclusionNotes: Array<string> = []
  const remaining: Array<string> = []
  let inInclusionSection = false

  for (const p of paragraphs) {
    const trimmed = p.trim()
    if (!trimmed) continue

    if (/^\*\*please\s+note/i.test(trimmed)) {
      inInclusionSection = true
      // Strip the "**Please Note:**" label prefix since the row already has
      // an "Inclusions" heading, but keep any remaining body text in the paragraph.
      const withoutLabel = trimmed.replace(/^\*\*please\s+note[:\s]*\*\*\s*/i, '').trim()
      if (withoutLabel) {
        inclusionNotes.push(withoutLabel)
      }
      continue
    }

    if (inInclusionSection && trimmed.startsWith('-')) {
      inclusionNotes.push(trimmed)
    } else {
      inInclusionSection = false
      remaining.push(trimmed)
    }
  }

  return {
    inclusionNotes: inclusionNotes.length ? inclusionNotes.join('\n\n') : undefined,
    remaining: remaining.join('\n\n'),
  }
}

/**
 * Extract date change policy from finePrint markdown text.
 * Matches paragraphs with a bold header containing "Date Change" keywords.
 */
export function extractDateChangesFromFinePrint(finePrint: string | undefined): {
  dateChanges: string | undefined
  remaining: string
} {
  if (!finePrint) return { dateChanges: undefined, remaining: '' }

  const paragraphs = finePrint.split('\n\n')
  const dateChanges: Array<string> = []
  const remaining: Array<string> = []

  for (const p of paragraphs) {
    const trimmed = p.trim()
    if (!trimmed) continue

    const heading = getBoldHeading(trimmed)
    if (heading && /date\s+change/i.test(heading)) {
      dateChanges.push(trimmed)
    } else {
      remaining.push(trimmed)
    }
  }

  return {
    dateChanges: dateChanges.length ? dateChanges.join('\n\n') : undefined,
    remaining: remaining.join('\n\n'),
  }
}
